import { computed, Injectable, signal } from "@angular/core";
import {
    ControlTab,
    RvbSliderDef,
    RvbSummary,
    RvbYearResult,
} from "../models/rent-vs-buy.models";
import { RentVsBuyDbType } from "@home-shark/types";
import { ApiHttpService } from "../../../../services/api.service";
import { take } from "rxjs";
import { RootState } from "../../../../reducers/root.reducer";
import { Store } from "@ngrx/store";
import { updateRentVsBuy } from "../actions/rent-vs-buy.actions";

const DEFAULT_INPUTS: RentVsBuyDbType = {
    homePrice: 400000,
    downPct: 20,
    closingCostPct: 3,
    interestRate: 6.8,
    loanYears: 30,
    propTaxRate: 1.1,
    homeIns: 0.5,
    hoaMonth: 0,
    maintPct: 1,
    sellerCostPct: 6,
    taxBracket: 24,
    rentIncrease: 3,
    rentersIns: 20,
    homeAppreciation: 3.5,
    marketReturn: 7,
    inflationRate: 2.5,
    horizon: 10,
};

export const SLIDER_DEFS: RvbSliderDef[] = [
    // BUY TAB
    {
        key: "homePrice",
        label: "Home price",
        min: 100000,
        max: 1500000,
        step: 5000,
        format: "dollar",
        tab: "buy",
        section: "Purchase price",
    },
    {
        key: "downPct",
        label: "Down payment",
        min: 3,
        max: 40,
        step: 1,
        format: "percent",
        tab: "buy",
    },
    {
        key: "closingCostPct",
        label: "Closing costs",
        min: 1,
        max: 5,
        step: 0.25,
        format: "percent",
        tab: "buy",
    },
    {
        key: "interestRate",
        label: "Mortgage rate",
        min: 2,
        max: 12,
        step: 0.1,
        format: "percent",
        tab: "buy",
        section: "Monthly costs",
    },
    {
        key: "loanYears",
        label: "Loan term",
        min: 10,
        max: 30,
        step: 5,
        format: "year",
        tab: "buy",
    },
    {
        key: "propTaxRate",
        label: "Property tax rate",
        min: 0.3,
        max: 3,
        step: 0.1,
        format: "percent",
        tab: "buy",
    },
    {
        key: "homeIns",
        label: "Homeowners insurance",
        min: 0.2,
        max: 1.5,
        step: 0.05,
        format: "percent",
        tab: "buy",
    },
    {
        key: "hoaMonth",
        label: "HOA / month",
        min: 0,
        max: 1200,
        step: 25,
        format: "dollar-month",
        tab: "buy",
    },
    {
        key: "maintPct",
        label: "Annual maintenance",
        min: 0.5,
        max: 3,
        step: 0.1,
        format: "percent",
        tab: "buy",
    },
    {
        key: "sellerCostPct",
        label: "Selling costs",
        min: 2,
        max: 10,
        step: 0.5,
        format: "percent",
        tab: "buy",
        section: "Exit",
    },
    {
        key: "taxBracket",
        label: "Marginal tax bracket",
        min: 10,
        max: 37,
        step: 1,
        format: "percent",
        tab: "buy",
        section: "Tax benefit",
    },
    // RENT TAB
    {
        key: "rentIncrease",
        label: "Annual rent increase",
        min: 0,
        max: 10,
        step: 0.25,
        format: "percent",
        tab: "rent",
        section: "Rent increases",
    },
    {
        key: "rentersIns",
        label: "Renter's insurance / mo",
        min: 5,
        max: 100,
        step: 5,
        format: "dollar-month",
        tab: "rent",
        section: "Other costs",
    },
    // MARKET TAB
    {
        key: "homeAppreciation",
        label: "Annual appreciation",
        min: 0,
        max: 8,
        step: 0.25,
        format: "percent",
        tab: "market",
        section: "Home appreciation",
    },
    {
        key: "marketReturn",
        label: "Market return (annual)",
        min: 2,
        max: 14,
        step: 0.25,
        format: "percent",
        tab: "market",
        section: "Opportunity cost",
    },
    {
        key: "inflationRate",
        label: "Inflation rate",
        min: 1,
        max: 6,
        step: 0.25,
        format: "percent",
        tab: "market",
        section: "Inflation",
    },
    // TIME TAB
    {
        key: "horizon",
        label: "Time horizon",
        min: 2,
        max: 30,
        step: 1,
        format: "year",
        tab: "time",
        section: "How long do you plan to stay?",
    },
];

@Injectable({ providedIn: "root" })
export class RentVsBuyService {
    readonly inputs = signal<RentVsBuyDbType>({ ...DEFAULT_INPUTS });
    readonly activeTab = signal<ControlTab>("buy");

    constructor(
        public api: ApiHttpService,
        public store: Store<RootState>
    ) {
        this.api
            .get<RentVsBuyDbType>("/users/componentState/rent-vs-buy")
            .pipe(take(1))
            .subscribe((data) => {
                this.inputs.update((oldInputs) => {
                    return { ...oldInputs, ...data };
                });
            });
    }

    private pmt(rate: number, nper: number, pv: number): number {
        if (rate === 0) return pv / nper;
        return (
            (pv * rate * Math.pow(1 + rate, nper)) /
            (Math.pow(1 + rate, nper) - 1)
        );
    }

    readonly initRent = computed(() => this.inputs().homePrice * 0.008);

    readonly yearResults = computed<RvbYearResult[]>(() => {
        const s = this.inputs();
        const hp = s.homePrice;
        const dp = (hp * s.downPct) / 100;
        const loan = hp - dp;
        const mr = s.interestRate / 100 / 12;
        const nper = s.loanYears * 12;
        const monthlyPI = this.pmt(mr, nper, loan);
        const propTaxMo = (hp * s.propTaxRate) / 100 / 12;
        const insMo = (hp * s.homeIns) / 100 / 12;
        const hoa = s.hoaMonth;
        const maintMo = (hp * s.maintPct) / 100 / 12;
        const closingCost = (hp * s.closingCostPct) / 100;
        const appRate = s.homeAppreciation / 100;
        const taxBracket = s.taxBracket / 100;
        const mktReturn = s.marketReturn / 100;
        const rentInc = s.rentIncrease / 100;
        const initRent = hp * 0.008;
        const rentersIns = s.rentersIns;

        const results: RvbYearResult[] = [];

        for (let y = 1; y <= s.horizon; y++) {
            // BUY path
            let totalOut = closingCost + dp;
            let bal = loan;
            let totInt = 0;
            let totPropTax = 0;
            for (let m = 0; m < y * 12; m++) {
                const ip = bal * mr;
                const pp = monthlyPI - ip;
                totInt += ip;
                totPropTax += propTaxMo;
                bal = Math.max(0, bal - pp);
                totalOut += monthlyPI + propTaxMo + insMo + hoa + maintMo;
            }
            const hv = hp * Math.pow(1 + appRate, y);
            const equityAfterSale = hv - bal - (hv * s.sellerCostPct) / 100;
            const deduct = (totInt + totPropTax) * taxBracket;
            const buyNetCost = totalOut - equityAfterSale - deduct;

            // RENT path
            let totalRent = 0;
            let port = dp + closingCost;
            let monthRent = initRent;
            for (let m = 0; m < y * 12; m++) {
                if (m > 0 && m % 12 === 0) monthRent *= 1 + rentInc;
                const buyMo = monthlyPI + propTaxMo + insMo + hoa + maintMo;
                const rentMo = monthRent + rentersIns;
                port *= 1 + mktReturn / 12;
                if (buyMo > rentMo) port += buyMo - rentMo;
                totalRent += rentMo;
            }
            const rentNetCost = totalRent - port;

            results.push({
                year: y,
                buyNetCost,
                rentNetCost,
                buyWealth: equityAfterSale,
                rentWealth: port,
            });
        }

        return results;
    });

    readonly summary = computed<RvbSummary>(() => {
        const s = this.inputs();
        const results = this.yearResults();
        const years = s.horizon;
        const last = results[years - 1];

        const hp = s.homePrice;
        const dp = (hp * s.downPct) / 100;
        const loan = hp - dp;
        const mr = s.interestRate / 100 / 12;
        const nper = s.loanYears * 12;
        const monthlyPI = this.pmt(mr, nper, loan);
        const propTaxMo = (hp * s.propTaxRate) / 100 / 12;
        const insMo = (hp * s.homeIns) / 100 / 12;
        const maintMo = (hp * s.maintPct) / 100 / 12;
        const closingCost = (hp * s.closingCostPct) / 100;
        const appRate = s.homeAppreciation / 100;
        const initRent = hp * 0.008;

        const monthlyBuyCost =
            monthlyPI + propTaxMo + insMo + s.hoaMonth + maintMo;

        // Breakeven
        let breakevenYear: number | null = null;
        for (let i = 0; i < results.length; i++) {
            if (results[i].buyNetCost < results[i].rentNetCost) {
                breakevenYear = i + 1;
                break;
            }
        }

        // Buy breakdown detail
        let bal = loan,
            totInt = 0;
        for (let m = 0; m < years * 12; m++) {
            const ip = bal * mr;
            totInt += ip;
            bal = Math.max(0, bal - (monthlyPI - ip));
        }
        const hvFinal = hp * Math.pow(1 + appRate, years);
        const sellerCostAmt = (hvFinal * s.sellerCostPct) / 100;
        const equityFinal = hvFinal - bal;
        const taxDeductBenefit =
            ((totInt + propTaxMo * years * 12) * s.taxBracket) / 100;

        // Rent breakdown detail
        let totalRentPd = 0,
            portFinal = dp + closingCost,
            monthRent = initRent;
        for (let m = 0; m < years * 12; m++) {
            if (m > 0 && m % 12 === 0) monthRent *= 1 + s.rentIncrease / 100;
            portFinal *= 1 + s.marketReturn / 100 / 12;
            const buyMo = monthlyPI + propTaxMo + insMo + s.hoaMonth + maintMo;
            if (buyMo > monthRent + s.rentersIns)
                portFinal += buyMo - monthRent - s.rentersIns;
            totalRentPd += monthRent + s.rentersIns;
        }
        const rentAtExit =
            initRent * Math.pow(1 + s.rentIncrease / 100, years - 1);

        return {
            winner: last.buyNetCost < last.rentNetCost ? "buy" : "rent",
            advantage: Math.abs(last.buyNetCost - last.rentNetCost),
            finalBuyNetCost: last.buyNetCost,
            finalRentNetCost: last.rentNetCost,
            monthlyBuyCost,
            monthlyRentStart: initRent,
            equityAtExit: equityFinal - sellerCostAmt,
            portfolioAtExit: last.rentWealth,
            breakevenYear,
            downPayment: dp,
            closingCosts: closingCost,
            totalMortgagePaid: monthlyPI * years * 12,
            totalInterestPaid: totInt,
            totalPropTaxPaid: propTaxMo * years * 12,
            totalInsPaid: insMo * years * 12,
            totalHoaPaid: s.hoaMonth * years * 12,
            totalMaintPaid: maintMo * years * 12,
            equityRecovered: equityFinal - sellerCostAmt,
            taxDeductBenefit,
            rentAtExit,
            totalRentPaid: totalRentPd,
            totalRentersInsPaid: s.rentersIns * years * 12,
            downPmtInvested: dp + closingCost,
            portfolioFinal: portFinal,
        };
    });

    updateInput(key: keyof RentVsBuyDbType, value: number): void {
        this.inputs.update((prev) => {
            const next = { ...prev, [key]: value };
            this.store.dispatch(updateRentVsBuy({ state: next }));
            return { ...prev, [key]: value };
        });
    }

    setActiveTab(tab: ControlTab): void {
        this.activeTab.set(tab);
    }
}
